import React from 'react';

import i18n from '@/utils/i18n';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  saveUserPreferences,
  selectOwnerProfilePrefs,
  selectCommsPrefs,
  selectBillingPrefs,
  selectPrivacyPrefs,
  defaultOwnerProfilePrefs,
  defaultCommsPrefs,
  defaultBillingPrefs,
  pickShape,
  PREFERENCE_KEYS,
  type CommCategory,
  type CommChannel,
  type CommsPrefs,
  type BillingPrefs,
} from '@/store/slices/userPreferences';
import { getTrackingConsent, saveTrackingConsent } from '@/nova/pages/configurator-v3/lib/analytics';

import type { SettingsProfile } from '../../mock-data/types';
import { validateBilling, type BillingErrors } from './components/BillingSection/billingValidation';
import { isSepaCountry } from './components/BillingSection/bankRules';

const SAVED_FLASH_MS = 1800;

/**
 * Shared save state around the prefs `saveUserPreferences` thunk. Kept LOCAL to
 * each form (rather than reading the slice's global `saving`/`saveError`) so one
 * section's save never lights up another's button — only one section is visible
 * at a time, but the local status is unambiguous. `justSaved` flashes "Saved ✓"
 * for a moment after a successful POST.
 */
type SaveStatus = {
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  /** Resolves true on a successful save. */
  persist: (payload: Record<string, unknown>) => Promise<boolean>;
};

const useSaveStatus = (): SaveStatus => {
  const dispatch = useAppDispatch();
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [justSaved, setJustSaved] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  // Guards the post-await setState below: the section can unmount (navigate back)
  // while a save is in flight.
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const persist = React.useCallback(
    async (payload: Record<string, unknown>): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      const result = await dispatch(saveUserPreferences(payload));
      if (!mounted.current) return saveUserPreferences.fulfilled.match(result);
      setSaving(false);
      if (saveUserPreferences.fulfilled.match(result)) {
        setJustSaved(true);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
        return true;
      }
      const message = (result.payload as { error?: string } | undefined)?.error;
      // Submit-time fallback message: built on demand in this callback, so the
      // i18n singleton (reads the current language at call time) is correct here.
      setSaveError(message ?? i18n.t('Could not save your changes'));
      return false;
    },
    [dispatch],
  );

  return { saving, saveError, justSaved, persist };
};

/* ---------- Profile ---------- */

/** The editable profile fields (everything except the auth-managed `email`). */
export type EditableProfileKey = Exclude<keyof SettingsProfile, 'email'>;

export type ProfileForm = {
  values: SettingsProfile;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onChange: (key: EditableProfileKey, value: string) => void;
  save: () => void;
};

export const useProfileForm = (): ProfileForm => {
  const email = useAppSelector((state) => state.authentication.user.email) ?? '';
  const prefs = useAppSelector(selectOwnerProfilePrefs);
  const { saving, saveError, justSaved, persist } = useSaveStatus();

  // email always from auth; the rest projected from preferences (pickShape drops
  // any field no longer in the schema, so a removed field never lingers).
  const build = React.useCallback(
    (): SettingsProfile => ({ email, ...pickShape(prefs, defaultOwnerProfilePrefs()) }),
    [email, prefs],
  );

  const [values, setValues] = React.useState<SettingsProfile>(build);
  const [baseline, setBaseline] = React.useState<SettingsProfile>(build);
  const baselineRef = React.useRef(baseline);
  baselineRef.current = baseline;

  // Re-seed when the server value (or email) changes — but keep unsaved edits:
  // only adopt the server value into the live form when it's pristine.
  React.useEffect(() => {
    const next = build();
    setValues((cur) => {
      const isDirty = (Object.keys(cur) as (keyof SettingsProfile)[]).some((k) => cur[k] !== baselineRef.current[k]);
      return isDirty ? cur : next;
    });
    setBaseline(next);
  }, [build]);

  const dirty = (Object.keys(values) as (keyof SettingsProfile)[]).some((k) => values[k] !== baseline[k]);

  const onChange = React.useCallback((key: EditableProfileKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = React.useCallback(() => {
    // email is auth-managed — persist only the profile preference fields, as the
    // full namespace object (the backend merges keys shallowly).
    void persist({
      [PREFERENCE_KEYS.ownerProfile]: {
        firstName: values.firstName,
        lastName: values.lastName,
        company: values.company,
        address: values.address,
      },
    });
  }, [persist, values]);

  return { values, dirty, saving, saveError, justSaved, onChange, save };
};

/* ---------- Communication preferences ---------- */

export type CommsForm = {
  comms: CommsPrefs;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  toggle: (category: CommCategory, channel: CommChannel) => void;
  save: () => void;
};

const sameComms = (a: CommsPrefs, b: CommsPrefs): boolean =>
  (Object.keys(a) as CommCategory[]).every((cat) =>
    (Object.keys(a[cat]) as CommChannel[]).every((ch) => a[cat][ch] === b[cat][ch]),
  );

export const useCommsForm = (): CommsForm => {
  const prefs = useAppSelector(selectCommsPrefs);
  const { saving, saveError, justSaved, persist } = useSaveStatus();

  // Merge persisted values over the defaults so a category/channel added later
  // still appears, then force the required security-email switch on.
  const build = React.useCallback((): CommsPrefs => {
    const base = defaultCommsPrefs();
    if (prefs) {
      (Object.keys(base) as CommCategory[]).forEach((cat) => {
        (Object.keys(base[cat]) as CommChannel[]).forEach((ch) => {
          const saved = prefs[cat]?.[ch];
          if (typeof saved === 'boolean') base[cat][ch] = saved;
        });
      });
    }
    base.security.email = true;
    return base;
  }, [prefs]);

  const [comms, setComms] = React.useState<CommsPrefs>(build);
  const [baseline, setBaseline] = React.useState<CommsPrefs>(build);
  const baselineRef = React.useRef(baseline);
  baselineRef.current = baseline;

  React.useEffect(() => {
    const next = build();
    setComms((cur) => (sameComms(cur, baselineRef.current) ? next : cur));
    setBaseline(next);
  }, [build]);

  const dirty = !sameComms(comms, baseline);

  const toggle = React.useCallback((category: CommCategory, channel: CommChannel) => {
    // The required security-email notification can't be switched off.
    if (category === 'security' && channel === 'email') return;
    setComms((prev) => ({
      ...prev,
      [category]: { ...prev[category], [channel]: !prev[category][channel] },
    }));
  }, []);

  const save = React.useCallback(() => {
    void persist({ [PREFERENCE_KEYS.comms]: comms });
  }, [persist, comms]);

  return { comms, dirty, saving, saveError, justSaved, toggle, save };
};

/* ---------- Privacy / tracking consent ---------- */

export type PrivacyForm = {
  marketing: boolean;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  toggle: () => void;
  save: () => void;
};

export const usePrivacyForm = (): PrivacyForm => {
  const prefs = useAppSelector(selectPrivacyPrefs);
  const { saving, saveError, justSaved, persist } = useSaveStatus();

  // Seed from the saved cross-device pref; before anything is saved, fall back to the
  // browser-scoped consent (localStorage / startProperties hand-off), and finally to ON — tracking
  // is on by default, so the toggle must reflect that reality when nothing explicit was chosen
  // (matches the app-wide gate in `resolveMarketingConsent`).
  const build = React.useCallback((): boolean => {
    if (prefs) return prefs.marketing === true;
    return getTrackingConsent()?.marketing ?? true;
  }, [prefs]);

  const [marketing, setMarketing] = React.useState<boolean>(build);
  const [baseline, setBaseline] = React.useState<boolean>(build);
  const baselineRef = React.useRef(baseline);
  baselineRef.current = baseline;

  React.useEffect(() => {
    const next = build();
    setMarketing((cur) => (cur === baselineRef.current ? next : cur));
    setBaseline(next);
  }, [build]);

  const dirty = marketing !== baseline;

  const toggle = React.useCallback(() => setMarketing((prev) => !prev), []);

  const save = React.useCallback(() => {
    // Persist cross-device AND mirror to the browser-scoped consent record so the pre-auth /
    // first-paint gate and the configurator view stay in sync. Preserve the existing `required`
    // flag; the single toggle drives both performance + marketing (one "Marketing & analytics").
    const current = getTrackingConsent();
    saveTrackingConsent({ required: current?.required ?? true, performance: marketing, marketing });
    void persist({ [PREFERENCE_KEYS.privacy]: { marketing } });
  }, [persist, marketing]);

  return { marketing, dirty, saving, saveError, justSaved, toggle, save };
};

/* ---------- Billing / withdrawal ---------- */

export type PayoutMethod = 'bank' | 'crypto';

export type BillingForm = {
  values: BillingPrefs;
  errors: BillingErrors;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onChange: (key: keyof BillingPrefs, value: string) => void;
  /** Turn a payout method (bank / crypto) on or off; the owner can set up one or both. */
  onToggleMethod: (method: PayoutMethod) => void;
  save: () => void;
};

const sameBilling = (a: BillingPrefs, b: BillingPrefs): boolean =>
  (Object.keys(a) as (keyof BillingPrefs)[]).every((k) => a[k] === b[k]);

/** Bank-detail fields whose presence marks a record as having a bank payout set up. */
const BANK_PRESENCE_KEYS: readonly (keyof BillingPrefs)[] = [
  'bankCountry', 'bankName', 'iban', 'accountNumber', 'sortCode', 'routingNumber', 'ifsc',
  'bsb', 'institutionNumber', 'transitNumber', 'clabe', 'cnapsCode', 'bankCode', 'branchCode',
];

export const useBillingForm = (): BillingForm => {
  const prefs = useAppSelector(selectBillingPrefs);
  const currencies = useAppSelector((state) => state.currencies.currenciesList) ?? [];
  const hasEur = currencies.includes('EUR');
  const { saving, saveError, justSaved, persist } = useSaveStatus();

  // Project saved prefs onto the current schema (pickShape drops any field we've
  // removed so it isn't carried forward and re-persisted on the next save).
  const build = React.useCallback((): BillingPrefs => {
    const shaped = pickShape(prefs, defaultBillingPrefs());
    // Records saved before payout methods existed carry no enable flags, so infer
    // them from which details are actually present: never show (and require) a
    // method the owner never set up. A blank legacy record falls back to bank.
    if (prefs && (prefs as unknown as Record<string, unknown>).bankEnabled === undefined) {
      const hasCrypto = String(shaped.walletAddress).trim() !== '';
      const hasBank = BANK_PRESENCE_KEYS.some((k) => String(shaped[k]).trim() !== '');
      shaped.cryptoEnabled = hasCrypto;
      shaped.bankEnabled = hasBank || !hasCrypto;
    }
    return shaped;
  }, [prefs]);

  const [values, setValues] = React.useState<BillingPrefs>(build);
  const [baseline, setBaseline] = React.useState<BillingPrefs>(build);
  const [errors, setErrors] = React.useState<BillingErrors>({});
  const baselineRef = React.useRef(baseline);
  baselineRef.current = baseline;

  React.useEffect(() => {
    const next = build();
    setValues((cur) => (sameBilling(cur, baselineRef.current) ? next : cur));
    setBaseline(next);
  }, [build]);

  const dirty = !sameBilling(values, baseline);

  const onChange = React.useCallback(
    (key: keyof BillingPrefs, value: string) => {
      // `value` is a plain string; `businessType` is a narrower union but the
      // Select only ever emits valid members, so the assertion is sound.
      setValues((prev) => {
        const next = { ...prev, [key]: value } as BillingPrefs;
        // SEPA accounts are paid in EUR by default. Seed EUR when the corridor
        // becomes SEPA and the currency is either unset or carried over from a
        // previous non-SEPA country (so switching US/USD to a SEPA country resets
        // to EUR rather than leaving an invalid SEPA-in-USD config). Only when EUR
        // is actually offered, and never overriding a choice made within SEPA.
        if (
          key === 'bankCountry' &&
          hasEur &&
          isSepaCountry(value) &&
          (prev.currency === '' || (!isSepaCountry(prev.bankCountry) && prev.currency !== 'EUR'))
        ) {
          next.currency = 'EUR';
        }
        // The asset list + memo requirement are network-specific, so clear them so
        // a stale token/tag from another chain can't survive a network switch.
        if (key === 'cryptoNetwork') {
          next.cryptoAsset = '';
          next.cryptoMemo = '';
        }
        return next;
      });
      // Clear the edited field's error; a network switch also clears the now-blank
      // asset/memo errors so a stale message doesn't linger on an empty field.
      setErrors((prev) => {
        const cleared: BillingErrors = { ...prev };
        let changed = false;
        if (prev[key]) {
          cleared[key] = undefined;
          changed = true;
        }
        if (key === 'cryptoNetwork') {
          if (prev.cryptoAsset) {
            cleared.cryptoAsset = undefined;
            changed = true;
          }
          if (prev.cryptoMemo) {
            cleared.cryptoMemo = undefined;
            changed = true;
          }
        }
        return changed ? cleared : prev;
      });
    },
    [hasEur],
  );

  const onToggleMethod = React.useCallback((method: PayoutMethod) => {
    const key = method === 'bank' ? 'bankEnabled' : 'cryptoEnabled';
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));
    // Toggling a method on/off resolves the "choose at least one" error.
    setErrors((prev) => (prev.payoutMethod ? { ...prev, payoutMethod: undefined } : prev));
  }, []);

  const save = React.useCallback(() => {
    const found = validateBilling(values);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return; // block save while any field is invalid
    // Project on write too: only the current schema's fields are persisted.
    void persist({ [PREFERENCE_KEYS.billing]: pickShape(values, defaultBillingPrefs()) });
  }, [persist, values]);

  return { values, errors, dirty, saving, saveError, justSaved, onChange, onToggleMethod, save };
};
